'use client';

import { useContext, useMemo } from 'react';
import { getLocalTimeZone, today } from '@internationalized/date';
import { useDateFormatter } from '@react-aria/i18n';
import { cn } from '@utils';
import {
	Button,
	CalendarCell,
	CalendarGrid,
	CalendarGridBody,
	CalendarGridHeader as CalendarGridHeaderPrimitive,
	CalendarHeaderCell,
	Calendar as CalendarPrimitive,
	CalendarStateContext,
	composeRenderProps,
	Text,
} from 'react-aria-components';

import type { CalendarProps as CalendarPrimitiveProps, DateValue } from 'react-aria-components';

import { ChevronLeft, ChevronRight } from '../icons';
import { iconStyle } from './Field';
import { Select } from './Select';

interface CalendarProps<T extends DateValue>
	extends Omit<CalendarPrimitiveProps<T>, 'visibleDuration'> {
	errorMessage?: string;
	className?: string;
}

type CalendarContextState = NonNullable<React.ContextType<typeof CalendarStateContext>>;

const CalendarDatePicker = <T extends DateValue>({
	errorMessage,
	className,
	minValue,
	maxValue,
	...props
}: CalendarProps<T>) => {
	const now = today(getLocalTimeZone());

	const isDateUnavailable = (date: DateValue) => {
		let isBeforeMinValue = false;
		let isAfterMaxValue = false;
		if (minValue) {
			isBeforeMinValue = date.compare(minValue) < 0;
		}
		if (maxValue) {
			isAfterMaxValue = date.compare(maxValue) > 0;
		}
		return isBeforeMinValue || isAfterMaxValue;
	};

	return (
		<CalendarPrimitive
			{...props}
			isDateUnavailable={isDateUnavailable}
			className="flex w-full flex-col items-center justify-center"
		>
			<CalendarHeader minValue={minValue} maxValue={maxValue} />
			<CalendarGrid
				weekdayStyle="short"
				className="[&_td]:border-collapse [&_td]:px-0 [&_td]:py-0.5"
			>
				<CalendarGridHeader />
				<CalendarGridBody>
					{date => (
						<CalendarCell
							date={date}
							className={composeRenderProps(
								className,
								(className, { isSelected, isOutsideMonth, isUnavailable }) =>
									cn(
										'text-light-text data-hovered:bg-light-background-shade relative flex size-10 cursor-pointer items-center justify-center rounded-xs text-xs tabular-nums outline-hidden sm:size-9 sm:text-sm/6',
										isSelected &&
											'bg-light-action-primary text-light-link-on-action data-hovered:bg-light-action-primary-hover data-invalid:bg-status-error data-pressed:bg-light-action-primary data-invalid:text-status-error-lightest',
										isOutsideMonth && 'text-neutral-dark',
										isUnavailable && 'text-light-text-lighter/25 cursor-not-allowed',
										date.compare(now) === 0 &&
											'after:bg-light-action-primary data-focus-visible:after:bg-light-link-on-action data-selected:after:bg-light-link-on-action after:pointer-events-none after:absolute after:start-1/2 after:bottom-1 after:z-10 after:size-[3px] after:-translate-x-1/2 after:rounded-full',
										className,
									),
							)}
						/>
					)}
				</CalendarGridBody>
			</CalendarGrid>
			{errorMessage && (
				<Text slot="errorMessage" className="text-status-error text-sm/6">
					{errorMessage}
				</Text>
			)}
		</CalendarPrimitive>
	);
};

const CalendarHeader = ({
	className,
	minValue,
	maxValue,
	...props
}: React.ComponentProps<'header'> & {
	minValue?: DateValue | null;
	maxValue?: DateValue | null;
}) => {
	const theme = 'light';
	const state = useContext(CalendarStateContext)!;
	const calendarIconStyle = cn(iconStyle({ theme }), 'size-icon-xs');

	const isNextDisabled = useMemo(() => {
		if (!maxValue) return false;

		// Get the last day of the currently visible month
		const currentMonth = state.focusedDate;
		const lastDayOfMonth = currentMonth.set({
			day: currentMonth.calendar.getDaysInMonth(currentMonth),
		});

		// Check if the last day of current month is >= maxValue
		return lastDayOfMonth.compare(maxValue) >= 0;
	}, [state.focusedDate, maxValue]);

	return (
		<header
			className={cn('flex w-full items-center justify-between gap-3 px-1 pb-4', className)}
			{...props}
		>
			{<SelectMonth state={state} />}
			{<SelectYear state={state} minValue={minValue} maxValue={maxValue} />}
			<div className="flex items-center gap-2">
				<Button slot="previous" className="items-center justify-center rounded-xs p-1">
					<ChevronLeft aria-hidden className={calendarIconStyle} />
				</Button>
				<Button
					slot="next"
					className={cn(
						'items-center justify-center rounded-xs p-1',
						isNextDisabled && 'cursor-not-allowed opacity-50',
					)}
					isDisabled={isNextDisabled}
				>
					<ChevronRight aria-hidden className={calendarIconStyle} />
				</Button>
			</div>
		</header>
	);
};

const SelectMonth = ({ state }: { state: CalendarContextState }) => {
	const formatter = useDateFormatter({
		month: 'short',
		timeZone: state.timeZone,
	});

	const months = useMemo(() => {
		const months = [];
		const numMonths = state.focusedDate.calendar.getMonthsInYear(state.focusedDate);

		for (let i = 1; i <= numMonths; i++) {
			const date = state.focusedDate.set({ month: i });
			months.push({ value: date.month, formatted: formatter.format(date.toDate(state.timeZone)) });
		}

		return months;
	}, [state.focusedDate, state.timeZone, formatter]);

	return (
		<Select
			focusOnOpen
			items={months}
			className="w-full"
			aria-label="Select month"
			selectedKey={state.focusedDate.month.toString() ?? (new Date().getMonth() + 1).toString()}
			onSelectionChange={value => {
				state.setFocusedDate(state.focusedDate.set({ month: Number(value) }));
			}}
		>
			{item => (
				<Select.Item
					key={item.value.toString()}
					id={item.value.toString()}
					textValue={item.formatted}
				>
					{item.formatted}
				</Select.Item>
			)}
		</Select>
	);
};

const SelectYear = ({
	state,
	minValue,
	maxValue = today(getLocalTimeZone()),
}: {
	state: CalendarContextState;
	minValue?: DateValue | null;
	maxValue?: DateValue | null;
}) => {
	const formatter = useDateFormatter({
		timeZone: state.timeZone,
	});

	const years = useMemo(() => {
		const currentYear = today(getLocalTimeZone()).year;
		const yearsList: { value: number; formatted: string }[] = [];

		for (let i = -20; i <= 20; i++) {
			const yearValue = currentYear + i;
			const isBeforeMinValue = minValue && yearValue < minValue.year;
			const isAfterMaxValue = maxValue && yearValue > maxValue.year;
			if (isBeforeMinValue || isAfterMaxValue) {
				continue;
			}

			const date = state.focusedDate.set({ year: yearValue });
			yearsList.push({
				value: yearValue,
				formatted: formatter.format(date.toDate(state.timeZone)),
			});
		}

		return yearsList;
	}, [state.focusedDate, minValue, maxValue, formatter, state.timeZone]);

	return (
		<Select
			focusOnOpen
			items={years}
			className="w-full"
			aria-label="Select year"
			selectedKey={state.focusedDate.year.toString()}
			onSelectionChange={value => {
				state.setFocusedDate(state.focusedDate.set({ year: Number(value) }));
			}}
		>
			{item => (
				<Select.Item id={item.value.toString()} key={item.value.toString()}>
					{item.value}
				</Select.Item>
			)}
		</Select>
	);
};

const CalendarGridHeader = () => {
	return (
		<CalendarGridHeaderPrimitive>
			{day => (
				<CalendarHeaderCell className="text-light-heading pb-2 text-xs sm:px-0 sm:py-0.5 lg:text-xs">
					{day}
				</CalendarHeaderCell>
			)}
		</CalendarGridHeaderPrimitive>
	);
};

export type { CalendarProps };
export { CalendarDatePicker, CalendarHeader, CalendarGridHeader };
